import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

const API_BASE_URL = (
  (typeof process !== "undefined" && process.env?.REACT_APP_MERCHANT_API_BASE_URL) ||
  "https://kdspanel-production.up.railway.app/api/merchant"
).replace(/\/+$/, "");

const PAGE_SIZE = 50;

const PAYMENT_OPTIONS = [
  { value: "all", label: "Tüm Ödeme Yöntemleri" },
  { value: "card", label: "Kredi Kartı" },
  { value: "cash", label: "Nakit" },
  { value: "multinet", label: "Multinet" },
  { value: "sodexo", label: "Sodexo" },
  { value: "edenred", label: "Edenred" },
  { value: "metropol", label: "Metropolcard" },
  { value: "setcard", label: "Setcard" },
];

const getAuthToken = () => localStorage.getItem("userToken");
const getAuthCard = () => localStorage.getItem("card");

const redirectToLogin = () => {
  localStorage.removeItem("userToken");
  localStorage.removeItem("card");
  window.location.href = "/mlogin";
};

const pad2 = (value) => String(value).padStart(2, "0");

const dateToInput = (date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

const getDefaultDates = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    startDate: dateToInput(firstDay),
    endDate: dateToInput(now),
  };
};

const toStartIso = (dateText) => new Date(`${dateText}T00:00:00.000`).toISOString();
const toEndIso = (dateText) => new Date(`${dateText}T23:59:59.999`).toISOString();

const formatTRY = (value) =>
  new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("tr-TR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const round4 = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 10000) / 10000;
const round2 = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

const getPaymentLabel = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  const item = PAYMENT_OPTIONS.find((option) => option.value === normalized);
  return item?.label || normalized || "-";
};

const getOrderKey = (order) => String(order?.id || order?._id || order?.orderNumber || "");

const itemQuantity = (item) => {
  const value = Number(item?.quantity ?? item?.qty ?? item?.adet ?? 1);
  return Number.isFinite(value) && value > 0 ? value : 1;
};

const optionQuantity = (option) => {
  const value = Number(option?.quantity ?? option?.qty ?? option?.adet ?? 1);
  return Number.isFinite(value) && value > 0 ? value : 1;
};

const getProductText = (order) =>
  (order?.items || [])
    .map((item) => `${itemQuantity(item)}x ${String(item?.name || "Ürün").trim()}`)
    .join(" | ");

const getOptionText = (order) => {
  const groups = [];
  for (const item of order?.items || []) {
    const options = (item?.options || []).map((option) => {
      const qty = optionQuantity(option);
      const total = Number.isFinite(Number(option?.totalPrice))
        ? Number(option.totalPrice)
        : Number(option?.unitPrice || option?.price || 0) * qty;
      return `${qty}x ${option?.name || "Opsiyon"} (${formatTRY(total)}, KDV %${Number(
        option?.kdvoption ?? option?.kdv ?? 0
      )}, D:${option?.departmanno ?? option?.departmentNo ?? "-"})`;
    });
    if (options.length) groups.push(`${item?.name || "Ürün"}: ${options.join(", ")}`);
  }
  return groups.join(" | ");
};

const getDepartmentText = (order) => {
  const seen = new Set();
  const rows = [];
  for (const item of order?.items || []) {
    const mainNo = String(item?.departmanno ?? item?.departmentNo ?? "").trim();
    const hasMainVat = item?.kdv !== undefined && item?.kdv !== null && item?.kdv !== "";
    const mainVat = Number(item?.kdv ?? 0);
    const mainKey = `Ürün-${mainNo}-${mainVat}`;
    if ((mainNo || hasMainVat) && !seen.has(mainKey)) {
      seen.add(mainKey);
      rows.push(`Ürün D:${mainNo || "-"} KDV:%${mainVat}`);
    }
    for (const option of item?.options || []) {
      const no = String(option?.departmanno ?? option?.departmentNo ?? "").trim();
      const hasOptionVat =
        (option?.kdvoption !== undefined && option?.kdvoption !== null && option?.kdvoption !== "") ||
        (option?.kdv !== undefined && option?.kdv !== null && option?.kdv !== "");
      const vat = Number(option?.kdvoption ?? option?.kdv ?? 0);
      const key = `Opsiyon-${no}-${vat}`;
      if ((no || hasOptionVat) && !seen.has(key)) {
        seen.add(key);
        rows.push(`Opsiyon D:${no || "-"} KDV:%${vat}`);
      }
    }
  }
  return rows.join(" | ");
};

async function apiPost(path, body, { signal } = {}) {
  const token = getAuthToken();
  const card = getAuthCard();
  if (!token || !card) {
    redirectToLogin();
    throw new Error("Giriş bilgisi bulunamadı.");
  }

  const response = await fetch(`${API_BASE_URL}/${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ card, ...(body || {}) }),
    signal,
  });

  let json = null;
  try {
    json = await response.json();
  } catch {
    json = null;
  }

  if (response.status === 401 || response.status === 403 || json?.redirectToLogin) {
    redirectToLogin();
    throw new Error("Oturum süresi doldu.");
  }

  if (!response.ok || json?.success === false) {
    throw new Error(json?.message || json?.error || `HTTP ${response.status}`);
  }

  return json;
}

function LeaveSelectionModal({ open, mode, count, onContinue, onCancel }) {
  if (!open) return null;

  const isFilter = mode === "filter";
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/45 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative w-full max-w-lg rounded-3xl bg-white p-7 shadow-2xl border border-slate-200">
        <div className="text-2xl font-black text-slate-900">
          {isFilter ? "Seçimler temizlensin mi?" : "Bu sayfadan ayrılmak istiyor musunuz?"}
        </div>
        <p className="mt-3 text-slate-600 leading-relaxed">
          {isFilter
            ? `${count} seçili siparişiniz var. Yeni filtre uygulanırsa seçili siparişler temizlenecek.`
            : `${count} seçili siparişiniz var. Sayfadan ayrılırsanız bu seçimler kaybolacak.`}
        </p>
        <div className="mt-7 grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-2xl border border-slate-300 bg-white px-5 py-3 font-bold text-slate-800 hover:bg-slate-50"
          >
            İptal
          </button>
          <button
            type="button"
            onClick={onContinue}
            className="rounded-2xl bg-slate-900 px-5 py-3 font-bold text-white hover:bg-black"
          >
            Devam Et
          </button>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ title, value, subtitle, className = "" }) {
  return (
    <div className={`rounded-2xl border bg-white p-5 shadow-sm ${className}`}>
      <div className="text-xs font-black uppercase tracking-wider text-slate-500">{title}</div>
      <div className="mt-2 text-2xl font-black text-slate-950">{value}</div>
      {subtitle ? <div className="mt-1 text-xs text-slate-500">{subtitle}</div> : null}
    </div>
  );
}

function Pagination({ page, totalPages, onChange }) {
  const pages = useMemo(() => {
    if (totalPages <= 9) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const result = [1];
    const start = Math.max(2, page - 2);
    const end = Math.min(totalPages - 1, page + 2);
    if (start > 2) result.push("left-ellipsis");
    for (let i = start; i <= end; i += 1) result.push(i);
    if (end < totalPages - 1) result.push("right-ellipsis");
    result.push(totalPages);
    return result;
  }, [page, totalPages]);

  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        className="h-10 rounded-xl border border-slate-300 bg-white px-4 font-bold text-slate-700 disabled:opacity-40"
      >
        ← Önceki
      </button>
      {pages.map((item) =>
        typeof item === "number" ? (
          <button
            type="button"
            key={item}
            onClick={() => onChange(item)}
            className={`h-10 min-w-10 rounded-xl px-3 font-black ${
              item === page ? "bg-slate-900 text-white" : "border border-slate-300 bg-white text-slate-700"
            }`}
          >
            {item}
          </button>
        ) : (
          <span key={item} className="px-1 text-slate-400">
            …
          </span>
        )
      )}
      <button
        type="button"
        disabled={page >= totalPages}
        onClick={() => onChange(page + 1)}
        className="h-10 rounded-xl border border-slate-300 bg-white px-4 font-bold text-slate-700 disabled:opacity-40"
      >
        Sonraki →
      </button>
    </div>
  );
}

function ExcelFis() {
  const navigate = useNavigate();
  const defaults = useMemo(() => getDefaultDates(), []);

  const [draftFilters, setDraftFilters] = useState({
    ...defaults,
    paymentMethod: "all",
    fisno: "",
    ordersayi: "",
    orderNumber: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({
    ...defaults,
    paymentMethod: "all",
    fisno: "",
    ordersayi: "",
    orderNumber: "",
  });
  const [page, setPage] = useState(1);
  const [sortDirection, setSortDirection] = useState("desc");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [summary, setSummary] = useState({
    totalOrders: 0,
    totalSales: 0,
    totalNet: 0,
    totalVat: 0,
    vatByRate: { 0: 0, 1: 0, 10: 0, 20: 0 },
    paymentMethodTotals: {},
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedById, setSelectedById] = useState({});
  const [leaveState, setLeaveState] = useState({ open: false, mode: "nav", path: "" });

  const selectedOrders = useMemo(() => Object.values(selectedById), [selectedById]);
  const selectedCount = selectedOrders.length;

  const fetchOrders = useCallback(async (signal) => {
    setLoading(true);
    setError("");
    try {
      const response = await apiPost(
        "excelfisorders",
        {
          startDate: toStartIso(appliedFilters.startDate),
          endDate: toEndIso(appliedFilters.endDate),
          paymentMethod: appliedFilters.paymentMethod,
          fisno: appliedFilters.fisno.trim(),
          ordersayi: appliedFilters.ordersayi.trim(),
          orderNumber: appliedFilters.orderNumber.trim(),
          page,
          limit: PAGE_SIZE,
          sortDirection,
        },
        { signal }
      );
      setItems(Array.isArray(response.items) ? response.items : []);
      setTotal(Number(response.total || 0));
      setTotalPages(Math.max(1, Number(response.totalPages || 1)));
      setSummary(
        response.summary || {
          totalOrders: 0,
          totalSales: 0,
          totalNet: 0,
          totalVat: 0,
          vatByRate: { 0: 0, 1: 0, 10: 0, 20: 0 },
          paymentMethodTotals: {},
        }
      );
    } catch (fetchError) {
      if (fetchError?.name === "AbortError") return;
      setError(fetchError?.message || "Siparişler alınamadı.");
      setItems([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page, sortDirection]);

  useEffect(() => {
    const controller = new AbortController();
    fetchOrders(controller.signal);
    return () => controller.abort();
  }, [fetchOrders]);

  useEffect(() => {
    const handler = (event) => {
      if (!selectedCount) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [selectedCount]);

  const applyFilters = useCallback(() => {
    if (!draftFilters.startDate || !draftFilters.endDate) {
      setError("Başlangıç ve bitiş tarihi zorunludur.");
      return;
    }
    if (new Date(draftFilters.startDate) > new Date(draftFilters.endDate)) {
      setError("Başlangıç tarihi bitiş tarihinden büyük olamaz.");
      return;
    }
    if (draftFilters.ordersayi && !/^\d+$/.test(draftFilters.ordersayi.trim())) {
      setError("Sipariş sayısı filtresi yalnızca rakam içermelidir.");
      return;
    }
    const commit = () => {
      setSelectedById({});
      setPage(1);
      setAppliedFilters({ ...draftFilters });
    };
    if (selectedCount > 0) {
      setLeaveState({ open: true, mode: "filter", path: "" });
      return;
    }
    commit();
  }, [draftFilters, selectedCount]);

  const continueLeaveAction = () => {
    if (leaveState.mode === "filter") {
      setLeaveState({ open: false, mode: "nav", path: "" });
      setSelectedById({});
      setPage(1);
      setAppliedFilters({ ...draftFilters });
      return;
    }
    const path = leaveState.path;
    setLeaveState({ open: false, mode: "nav", path: "" });
    if (path) navigate(path);
  };

  const requestNavigate = (path) => {
    if (selectedCount > 0) {
      setLeaveState({ open: true, mode: "nav", path });
      return;
    }
    navigate(path);
  };

  const toggleOrder = (order) => {
    const key = getOrderKey(order);
    if (!key) return;
    setSelectedById((current) => {
      const next = { ...current };
      if (next[key]) delete next[key];
      else next[key] = order;
      return next;
    });
  };

  const allCurrentPageSelected = items.length > 0 && items.every((order) => selectedById[getOrderKey(order)]);

  const toggleCurrentPage = () => {
    setSelectedById((current) => {
      const next = { ...current };
      if (allCurrentPageSelected) {
        for (const order of items) delete next[getOrderKey(order)];
      } else {
        for (const order of items) next[getOrderKey(order)] = order;
      }
      return next;
    });
  };

  const selectedSummary = useMemo(() => {
    const initial = { totalSales: 0, totalNet: 0, totalVat: 0, vatByRate: { 0: 0, 1: 0, 10: 0, 20: 0 } };
    for (const order of selectedOrders) {
      const vat = order?.vatSummary || {};
      initial.totalSales += Number(vat.gross ?? order?.totalPrice ?? 0);
      initial.totalNet += Number(vat.net || 0);
      initial.totalVat += Number(vat.vat || 0);
      for (const rate of [0, 1, 10, 20]) {
        initial.vatByRate[rate] += Number(vat?.vatByRate?.[rate] ?? vat?.vatByRate?.[String(rate)] ?? 0);
      }
    }
    initial.totalSales = round2(initial.totalSales);
    initial.totalNet = round2(initial.totalNet);
    initial.totalVat = round2(initial.totalVat);
    return initial;
  }, [selectedOrders]);

  const exportExcel = () => {
    if (!selectedOrders.length) return;

    const sortedOrders = [...selectedOrders].sort((a, b) => {
      const av = new Date(a?.reportDate || 0).getTime();
      const bv = new Date(b?.reportDate || 0).getTime();
      return sortDirection === "asc" ? av - bv : bv - av;
    });

    const orderRows = sortedOrders.map((order) => {
      const vat = order?.vatSummary || {};
      return {
        Tarih: formatDateTime(order.reportDate),
        "Sipariş Sayısı": order.ordersayi ?? "",
        "Order Number": String(order.orderNumber || ""),
        "Fiş No": String(order.fisno || ""),
        "Ödeme Yöntemi": getPaymentLabel(order.paymentMethod),
        Durum: "approved",
        Kiosk: order.kiosk || "",
        "Sipariş Tipi": order.orderType || order.selectedOrderType || "",
        Ürünler: getProductText(order),
        Opsiyonlar: getOptionText(order),
        Departmanlar: getDepartmentText(order),
        "Toplam Brüt": round2(vat.gross ?? order.totalPrice ?? 0),
        "Toplam Net": round2(vat.net || 0),
        "Toplam KDV": round2(vat.vat || 0),
        "KDV %20": round2(vat?.vatByRate?.[20] ?? vat?.vatByRate?.["20"] ?? 0),
        "KDV %10": round2(vat?.vatByRate?.[10] ?? vat?.vatByRate?.["10"] ?? 0),
        "KDV %1": round2(vat?.vatByRate?.[1] ?? vat?.vatByRate?.["1"] ?? 0),
        "KDV %0": round2(vat?.vatByRate?.[0] ?? vat?.vatByRate?.["0"] ?? 0),
        "Transaction ID": String(order.transactionid || ""),
        "Document ID": String(order.documentid || ""),
      };
    });

    const detailRows = [];
    for (const order of sortedOrders) {
      for (const item of order?.items || []) {
        const qty = itemQuantity(item);
        const unit = Number(item?.basePrice ?? item?.price ?? 0);
        const gross = round4(unit * qty);
        const rate = Number(item?.kdv ?? 0);
        const net = rate >= 0 ? round4(gross / (1 + rate / 100)) : gross;
        detailRows.push({
          Tarih: formatDateTime(order.reportDate),
          "Sipariş Sayısı": order.ordersayi ?? "",
          "Order Number": String(order.orderNumber || ""),
          "Fiş No": String(order.fisno || ""),
          "Ödeme Yöntemi": getPaymentLabel(order.paymentMethod),
          Kiosk: order.kiosk || "",
          Satır: "Ürün",
          Ürün: item?.name || "",
          Opsiyon: "",
          Adet: qty,
          "Birim Fiyat": round2(unit),
          Brüt: round2(gross),
          "KDV Oranı": rate,
          Net: round2(net),
          KDV: round2(gross - net),
          Departman: String(item?.departmanno ?? item?.departmentNo ?? ""),
        });

        for (const option of item?.options || []) {
          const optionQty = optionQuantity(option);
          const optionUnit = Number(option?.unitPrice ?? option?.price ?? 0);
          const optionGross = Number.isFinite(Number(option?.totalPrice))
            ? Number(option.totalPrice)
            : optionUnit * optionQty;
          const optionRate = Number(option?.kdvoption ?? option?.kdv ?? 0);
          const optionNet = optionRate >= 0 ? round4(optionGross / (1 + optionRate / 100)) : optionGross;
          detailRows.push({
            Tarih: formatDateTime(order.reportDate),
            "Sipariş Sayısı": order.ordersayi ?? "",
            "Order Number": String(order.orderNumber || ""),
            "Fiş No": String(order.fisno || ""),
            "Ödeme Yöntemi": getPaymentLabel(order.paymentMethod),
            Kiosk: order.kiosk || "",
            Satır: "Opsiyon",
            Ürün: item?.name || "",
            Opsiyon: option?.name || "",
            Adet: optionQty,
            "Birim Fiyat": round2(optionUnit),
            Brüt: round2(optionGross),
            "KDV Oranı": optionRate,
            Net: round2(optionNet),
            KDV: round2(optionGross - optionNet),
            Departman: String(option?.departmanno ?? option?.departmentNo ?? ""),
          });
        }
      }
    }

    const summaryRows = [
      { Açıklama: "Seçilen Sipariş Sayısı", Değer: selectedOrders.length },
      { Açıklama: "Toplam Satış", Değer: selectedSummary.totalSales },
      { Açıklama: "Toplam Net", Değer: selectedSummary.totalNet },
      { Açıklama: "Toplam KDV", Değer: selectedSummary.totalVat },
      { Açıklama: "KDV %20", Değer: round2(selectedSummary.vatByRate[20]) },
      { Açıklama: "KDV %10", Değer: round2(selectedSummary.vatByRate[10]) },
      { Açıklama: "KDV %1", Değer: round2(selectedSummary.vatByRate[1]) },
      { Açıklama: "KDV %0", Değer: round2(selectedSummary.vatByRate[0]) },
      { Açıklama: "Başlangıç Tarihi", Değer: appliedFilters.startDate },
      { Açıklama: "Bitiş Tarihi", Değer: appliedFilters.endDate },
    ];

    const workbook = XLSX.utils.book_new();
    const ordersSheet = XLSX.utils.json_to_sheet(orderRows);
    const detailSheet = XLSX.utils.json_to_sheet(detailRows);
    const summarySheet = XLSX.utils.json_to_sheet(summaryRows);

    ordersSheet["!cols"] = [
      { wch: 18 }, { wch: 14 }, { wch: 34 }, { wch: 12 }, { wch: 18 }, { wch: 12 },
      { wch: 12 }, { wch: 14 }, { wch: 42 }, { wch: 70 }, { wch: 42 }, { wch: 14 },
      { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
      { wch: 18 }, { wch: 18 },
    ];
    detailSheet["!cols"] = [
      { wch: 18 }, { wch: 14 }, { wch: 34 }, { wch: 12 }, { wch: 18 }, { wch: 12 },
      { wch: 12 }, { wch: 28 }, { wch: 35 }, { wch: 9 }, { wch: 13 }, { wch: 13 },
      { wch: 12 }, { wch: 13 }, { wch: 13 }, { wch: 13 },
    ];
    summarySheet["!cols"] = [{ wch: 28 }, { wch: 22 }];
    ordersSheet["!autofilter"] = { ref: ordersSheet["!ref"] || "A1:A1" };
    detailSheet["!autofilter"] = { ref: detailSheet["!ref"] || "A1:A1" };

    XLSX.utils.book_append_sheet(workbook, ordersSheet, "Siparişler");
    XLSX.utils.book_append_sheet(workbook, detailSheet, "Ürün Detayları");
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Özet");

    const fileName = `excel-fis_${appliedFilters.startDate}_${appliedFilters.endDate}_${selectedOrders.length}-siparis.xlsx`;
    XLSX.writeFile(workbook, fileName, { compression: true });
  };

  const paymentSummaryEntries = Object.entries(summary.paymentMethodTotals || {}).filter(([, value]) => Number(value) > 0);

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <LeaveSelectionModal
        open={leaveState.open}
        mode={leaveState.mode}
        count={selectedCount}
        onContinue={continueLeaveAction}
        onCancel={() => setLeaveState({ open: false, mode: "nav", path: "" })}
      />

      <div className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1800px] flex-col gap-4 px-5 py-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <h1 className="text-3xl font-black tracking-tight">Excel Fiş</h1>
            <p className="mt-1 text-sm text-slate-500">Yalnızca onaylanmış siparişler ve giriş yaptığınız işletme gösterilir.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => requestNavigate("/")} className="rounded-xl bg-emerald-600 px-4 py-2.5 font-bold text-white hover:bg-emerald-700">Ürün Paneli</button>
            <button onClick={() => requestNavigate("/designpanel")} className="rounded-xl bg-sky-600 px-4 py-2.5 font-bold text-white hover:bg-sky-700">Dizayn Paneli</button>
            <button onClick={() => requestNavigate("/ReceiptInfoPage")} className="rounded-xl bg-purple-600 px-4 py-2.5 font-bold text-white hover:bg-purple-700">Fiş Bilgi</button>
            <button onClick={() => requestNavigate("/preferance")} className="rounded-xl bg-teal-600 px-4 py-2.5 font-bold text-white hover:bg-teal-700">Tercihler</button>
            <button onClick={() => requestNavigate("/branches")} className="rounded-xl bg-slate-800 px-4 py-2.5 font-bold text-white hover:bg-slate-900">Şubeler</button>
            <button onClick={() => requestNavigate("/appointments")} className="rounded-xl bg-fuchsia-600 px-4 py-2.5 font-bold text-white hover:bg-fuchsia-700">Randevular</button>
            <button type="button" disabled className="rounded-xl bg-amber-500 px-4 py-2.5 font-black text-white opacity-90">Excel Fiş</button>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-[1800px] space-y-5 px-5 py-6">
        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-7">
            <label className="space-y-1">
              <span className="text-xs font-black uppercase text-slate-500">Başlangıç Tarihi</span>
              <input type="date" value={draftFilters.startDate} onChange={(event) => setDraftFilters((current) => ({ ...current, startDate: event.target.value }))} className="h-11 w-full rounded-xl border border-slate-300 px-3 outline-none focus:border-slate-900" />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-black uppercase text-slate-500">Bitiş Tarihi</span>
              <input type="date" value={draftFilters.endDate} onChange={(event) => setDraftFilters((current) => ({ ...current, endDate: event.target.value }))} className="h-11 w-full rounded-xl border border-slate-300 px-3 outline-none focus:border-slate-900" />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-black uppercase text-slate-500">Ödeme Yöntemi</span>
              <select value={draftFilters.paymentMethod} onChange={(event) => setDraftFilters((current) => ({ ...current, paymentMethod: event.target.value }))} className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 outline-none focus:border-slate-900">
                {PAYMENT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
            <label className="space-y-1">
              <span className="text-xs font-black uppercase text-slate-500">Fiş No</span>
              <input value={draftFilters.fisno} onChange={(event) => setDraftFilters((current) => ({ ...current, fisno: event.target.value }))} placeholder="Örn. 8" className="h-11 w-full rounded-xl border border-slate-300 px-3 outline-none focus:border-slate-900" />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-black uppercase text-slate-500">Ordersayi</span>
              <input inputMode="numeric" value={draftFilters.ordersayi} onChange={(event) => setDraftFilters((current) => ({ ...current, ordersayi: event.target.value.replace(/[^\d]/g, "") }))} placeholder="Örn. 12" className="h-11 w-full rounded-xl border border-slate-300 px-3 outline-none focus:border-slate-900" />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-black uppercase text-slate-500">Order Number</span>
              <input value={draftFilters.orderNumber} onChange={(event) => setDraftFilters((current) => ({ ...current, orderNumber: event.target.value }))} placeholder="Sipariş kodu" className="h-11 w-full rounded-xl border border-slate-300 px-3 outline-none focus:border-slate-900" />
            </label>
            <div className="flex items-end">
              <button type="button" onClick={applyFilters} disabled={loading} className="h-11 w-full rounded-xl bg-slate-900 px-5 font-black text-white hover:bg-black disabled:opacity-60">
                {loading ? "Getiriliyor…" : "Siparişleri Getir"}
              </button>
            </div>
          </div>
          {error ? <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 font-semibold text-red-700">{error}</div> : null}
        </section>

        <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-8">
          <SummaryCard title="Sipariş" value={Number(summary.totalOrders || 0).toLocaleString("tr-TR")} subtitle="Aktif filtre sonucu" />
          <SummaryCard title="Toplam Satış" value={formatTRY(summary.totalSales)} />
          <SummaryCard title="Toplam Net" value={formatTRY(summary.totalNet)} />
          <SummaryCard title="Toplam KDV" value={formatTRY(summary.totalVat)} className="border-amber-200" />
          <SummaryCard title="KDV %20" value={formatTRY(summary?.vatByRate?.[20] ?? summary?.vatByRate?.["20"] ?? 0)} />
          <SummaryCard title="KDV %10" value={formatTRY(summary?.vatByRate?.[10] ?? summary?.vatByRate?.["10"] ?? 0)} />
          <SummaryCard title="KDV %1" value={formatTRY(summary?.vatByRate?.[1] ?? summary?.vatByRate?.["1"] ?? 0)} />
          <SummaryCard title="KDV %0" value={formatTRY(summary?.vatByRate?.[0] ?? summary?.vatByRate?.["0"] ?? 0)} />
        </section>

        {paymentSummaryEntries.length ? (
          <section className="flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
            {paymentSummaryEntries.map(([method, value]) => (
              <span key={method} className="rounded-full bg-slate-100 px-3 py-1.5 text-sm font-bold text-slate-700">
                {getPaymentLabel(method)}: {Number(value).toLocaleString("tr-TR")}
              </span>
            ))}
          </section>
        ) : null}

        <section className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="flex flex-col gap-3 border-b border-slate-200 p-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <div className="rounded-xl bg-amber-100 px-4 py-2 font-black text-amber-900">Toplam seçilen sipariş: {selectedCount}</div>
              <button type="button" onClick={toggleCurrentPage} disabled={!items.length} className="rounded-xl border border-slate-300 bg-white px-4 py-2 font-bold text-slate-800 hover:bg-slate-50 disabled:opacity-50">
                {allCurrentPageSelected ? "Bu Sayfanın Seçimini Kaldır" : "Bu Sayfadaki Siparişleri Seç"}
              </button>
              {selectedCount > 0 ? (
                <button type="button" onClick={() => setSelectedById({})} className="rounded-xl border border-red-200 bg-red-50 px-4 py-2 font-bold text-red-700 hover:bg-red-100">Tüm Seçimleri Temizle</button>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-2">
              <select value={sortDirection} onChange={(event) => { setSortDirection(event.target.value); setPage(1); }} className="h-11 rounded-xl border border-slate-300 bg-white px-3 font-bold">
                <option value="desc">Tarih: Yeniden Eskiye</option>
                <option value="asc">Tarih: Eskiden Yeniye</option>
              </select>
              <button type="button" onClick={exportExcel} disabled={!selectedCount} className="h-11 rounded-xl bg-emerald-600 px-5 font-black text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300">
                Tarihe Göre Sırala ve Excel Olarak İndir
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-[1900px] w-full border-collapse text-sm">
              <thead>
                <tr className="text-left text-xs font-black uppercase tracking-wide text-slate-800">
                  <th className="sticky left-0 z-20 border border-slate-300 bg-slate-200 p-3 text-center">
                    <input type="checkbox" checked={allCurrentPageSelected} onChange={toggleCurrentPage} aria-label="Bu sayfanın tümünü seç" className="h-5 w-5" />
                  </th>
                  <th className="border border-slate-300 bg-emerald-200 p-3">Tarih</th>
                  <th className="border border-slate-300 bg-yellow-200 p-3">Ordersayi</th>
                  <th className="border border-slate-300 bg-cyan-200 p-3">Order Number</th>
                  <th className="border border-slate-300 bg-violet-200 p-3">Fiş No</th>
                  <th className="border border-slate-300 bg-orange-200 p-3">Ödeme</th>
                  <th className="border border-slate-300 bg-blue-200 p-3">Kiosk</th>
                  <th className="border border-slate-300 bg-lime-200 p-3">Ürünler</th>
                  <th className="border border-slate-300 bg-rose-200 p-3">Opsiyonlar</th>
                  <th className="border border-slate-300 bg-indigo-200 p-3">Departmanlar</th>
                  <th className="border border-slate-300 bg-emerald-200 p-3 text-right">Toplam</th>
                  <th className="border border-slate-300 bg-amber-200 p-3 text-right">KDV</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={12} className="p-14 text-center"><div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-slate-300 border-t-slate-900" /><div className="mt-3 font-bold text-slate-600">Siparişler yükleniyor…</div></td></tr>
                ) : items.length ? (
                  items.map((order) => {
                    const key = getOrderKey(order);
                    const selected = !!selectedById[key];
                    return (
                      <tr key={key} onClick={() => toggleOrder(order)} className={`cursor-pointer align-top ${selected ? "bg-amber-50" : "odd:bg-white even:bg-slate-50 hover:bg-sky-50"}`}>
                        <td className="sticky left-0 z-10 border border-slate-300 bg-inherit p-3 text-center" onClick={(event) => event.stopPropagation()}>
                          <input type="checkbox" checked={selected} onChange={() => toggleOrder(order)} className="h-5 w-5" />
                        </td>
                        <td className="border border-slate-300 p-3 whitespace-nowrap font-semibold">{formatDateTime(order.reportDate)}</td>
                        <td className="border border-slate-300 p-3 text-center text-lg font-black">{order.ordersayi ?? ""}</td>
                        <td className="border border-slate-300 p-3 font-mono text-xs break-all">{order.orderNumber || ""}</td>
                        <td className="border border-slate-300 p-3 text-center font-black">{order.fisno || ""}</td>
                        <td className="border border-slate-300 p-3 font-bold">{getPaymentLabel(order.paymentMethod)}</td>
                        <td className="border border-slate-300 p-3 font-semibold">{order.kiosk || ""}</td>
                        <td className="border border-slate-300 p-3 min-w-[260px] font-semibold">{getProductText(order) || "-"}</td>
                        <td className="border border-slate-300 p-3 min-w-[430px] text-xs leading-relaxed">{getOptionText(order) || "-"}</td>
                        <td className="border border-slate-300 p-3 min-w-[300px] text-xs leading-relaxed">{getDepartmentText(order) || "-"}</td>
                        <td className="border border-slate-300 p-3 text-right text-lg font-black">{formatTRY(order?.vatSummary?.gross ?? order.totalPrice)}</td>
                        <td className="border border-slate-300 p-3 text-right font-black text-amber-700">{formatTRY(order?.vatSummary?.vat || 0)}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr><td colSpan={12} className="p-14 text-center text-lg font-bold text-slate-500">Seçilen filtrelerde approved sipariş bulunamadı.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-4 border-t border-slate-200 p-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="font-semibold text-slate-600">Toplam {total.toLocaleString("tr-TR")} sipariş · Sayfa {page}/{totalPages} · Sayfa başına {PAGE_SIZE}</div>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        </section>
      </main>
    </div>
  );
}

export default ExcelFis;
